import { FC, createContext, useState } from 'react';
import {
  BaseNodeItemChange,
  DesignIntent,
  DesignObject,
  DesignModule,
  DesignVariable,
  ComponentWithOnlyChildrenProp
} from '../types';

type ChangeSource = 'current' | 'new';
type ChangeItem = BaseNodeItemChange<DesignIntent | DesignObject | DesignModule | DesignVariable>;

interface FinalChangeMap {
  itemID: string;
  changeMap: ChangeItem;
  changeSource: ChangeSource;
  key: string;
  itemTypeList: string;
  cmItem: string;
}
type FinalChangeMaps = Record<string, FinalChangeMap>;

type ConflictItem = Omit<FinalChangeMap, 'changeMap' | 'changeSource'> & {
  currentChangeMap: ChangeItem;
  incomingChangeMap: ChangeItem;
  parentComponentId: string;
};

interface MergeConflictsContextType {
  collapsedConflictPanelIds: Set<string>;
  allConflictItems: ConflictItem[];
  finalChangeMaps: FinalChangeMaps;
  toggleCollapseConflictPanel: (id: string) => void;
  addToAllConflictItems: (items: ConflictItem[]) => void;
  addToFinalChangeMaps: (id: string, changeSource: ChangeSource) => void;
}

// https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/context/#type-assertion-as-an-alternative
export const MergeConflictsContext = createContext<MergeConflictsContextType>(null!);

export const MergeConflictsContextProvider: FC<ComponentWithOnlyChildrenProp> = ({ children }) => {
  const [collapsedConflictPanelIds, setCollapsedConflictPanelIds] = useState<Set<string>>(new Set());
  const [allConflictItems, setAllConflictItems] = useState<ConflictItem[]>([]);
  const [finalChangeMaps, setFinalChangeMaps] = useState<FinalChangeMaps>({});

  /**
   * Toggle the collapsed/expanded state of conflict panels.
   * @param id The item ID that the conflict is for.
  */
  const toggleCollapseConflictPanel = (id: string) => {
    const collapsed = new Set(collapsedConflictPanelIds);
    if (collapsed.has(id)) {
      collapsed.delete(id);
    } {
      collapsed.add(id);
    }
    setCollapsedConflictPanelIds(collapsed);
  };

  /**
   * Update an array containing all the conflicting items.
   * @param items An array with conflicting items.
   */
  const addToAllConflictItems = (items: ConflictItem[]) => {
    const newItems = allConflictItems.concat(items);
    setAllConflictItems(newItems);
  };

  /**
   * Called when selecting a readio button value from the conflicts panel to pick it as final changeset.
   * @param item The change map item.
   */
  const addToFinalChangeMaps = (id: string, changeSource: ChangeSource) => {
    const conflictItem = allConflictItems.find((x) => x.itemID === id);

    if (conflictItem) {
      const newItem: FinalChangeMap = {
        itemID: id,
        changeMap: changeSource === 'current' ? conflictItem.currentChangeMap : conflictItem.incomingChangeMap,
        changeSource,
        key: conflictItem.key,
        itemTypeList: conflictItem.itemTypeList,
        cmItem: conflictItem.cmItem
      };
      const newFinalChangeMaps = {
        ...finalChangeMaps,
        [id]: newItem
      };

      // Auto-resolve other conflicts of the same type and their parents.
      allConflictItems.forEach((item) => {
        // Update parent component and all other component instances
        // when selecting an item with a parent component.
        if (conflictItem.parentComponentId) {
          // All other items that have the same parent component ID.
          // or the parent itself.
          if ([item.parentComponentId, item.itemID].includes(conflictItem.parentComponentId)) {
            newFinalChangeMaps[item.itemID] = {
              itemID: item.itemID,
              changeMap: changeSource === 'current' ? item.currentChangeMap : item.incomingChangeMap,
              changeSource,
              key: item.key,
              itemTypeList: item.itemTypeList,
              cmItem: item.cmItem
            };
          }
        }

        // Update all child of a parent when selecting a parent component.
        if (!!item.parentComponentId && item.parentComponentId === conflictItem.itemID) {
          newFinalChangeMaps[item.itemID] = {
            itemID: item.itemID,
            changeMap: changeSource === 'current' ? item.currentChangeMap : item.incomingChangeMap,
            changeSource,
            key: item.key,
            itemTypeList: item.itemTypeList,
            cmItem: item.cmItem
          };
        }
      });

      setFinalChangeMaps(newFinalChangeMaps);
    }

    removeUnresolvedWrapperErrorClass();
  };

  /**
   * Remove the `unresolved` CSS class from the `table` element of the first unresolved conflict wrapper.
   * @see `MergeConflictDialog.attemptToAcceptChanges` when clicking the `Accept Changes` button.
  */
  const removeUnresolvedWrapperErrorClass = () => {
    document.querySelector('.conflict_wrapper[data-is-resolved="false"] table.unresolved')?.classList?.remove('unresolved');
  };

  const contextValue: MergeConflictsContextType = {
    collapsedConflictPanelIds,
    allConflictItems,
    finalChangeMaps,
    toggleCollapseConflictPanel,
    addToAllConflictItems,
    addToFinalChangeMaps
  };

  return (
    <MergeConflictsContext.Provider value={contextValue}>
      {children}
    </MergeConflictsContext.Provider>
  );
};
